import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { numberToCurrencyFormatter } from './helper';
import { getXaxisLabels } from './utilizattionProcedureHelper';
import { Columns } from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/BasicTable';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React from 'react';

const filterSavingsProgram = (data: any) => {
  const filteredPrograms = data.filter((program: any) => {
    const costSavings = parseFloat(program.totalCostSavings);
    return !isNaN(costSavings) && costSavings > 0;
  });
  return filteredPrograms;
};

export const formatSavingsUtilizationTopCards = (
  data: any,
  setCostSavings: any,
  setPrescriptionFills: any,
  setRxCost?: any,
  setPlanPaid?: any,
) => {
  let totalCostSavings: number = 0;
  let totalPrescriptionFills: number = 0;
  let totalRxCost: number = 0;
  let totalPlanPaid: number = 0;

  const filteredPrograms = filterSavingsProgram(data);
  const costSavings = filteredPrograms?.map((item: any) => {
    totalCostSavings += item?.totalCostSavings > 0 ? item?.totalCostSavings : 0;

    if (item?.totalCostSavings > 0) {
      return {
        cost: item?.totalCostSavings,
        program: item?.savings_program,
      };
    }
    return {};
  });
  const prescriptionFills = data?.map((item: any) => {
    totalPrescriptionFills += item?.claimsCount;
    return {
      fills: item?.claimsCount,
      program: item?.savings_program,
    };
  });

  const rxCost = data?.map((item: any) => {
    totalRxCost += item?.totalCost;
    return {
      cost: item?.totalCost,
      program: item?.savings_program,
    };
  });
  const planPaid = data?.map((item: any) => {
    totalPlanPaid += item?.totalPlanPaid;
    return {
      cost: item?.totalPlanPaid,
      program: item?.savings_program,
    };
  });
  setCostSavings({
    totalSavings: totalCostSavings,
    costSavings,
  });
  setPrescriptionFills({
    totalfills: totalPrescriptionFills,
    prescriptionFills,
  });
  setRxCost({
    totalCost: totalRxCost,
    rxCost,
  });
  setPlanPaid({
    totalPlanPaid,
    planPaid,
  });
};

export const formatFillsBreakdown = (data: any, toggleState: string) => {
  const costTrendData: {
    seriesname: string;
    plottooltext: string;
    data: any[];
    color: string;
  }[] = [];

  const colors = ['#96A3D9', '#9DDDEB', '#FFDBA5', '#FFF6A7', '#CFA5BD'];

  data?.costTrend?.map((item: any, index: number) => {
    const costTrend: any[] = [];

    item?.costTrendData?.map((month: any) => {
      costTrend.push({
        value: toggleState === 'left' ? month?.planPaid : month?.claimsCount,
      });
    });
    costTrendData.push({
      seriesname: item?.savings_program,
      plottooltext: `${item?.savings_program} - $dataValue`,
      data: costTrend,
      color: colors[index],
    });
  });

  const xAxisValues: { label: string }[] = [];
  let currentMonth = data?.startFromMonth;
  let currentYear = data?.startFromYear;
  const yearChangeValueOfI = 12 - data?.startFromMonth + 1;

  for (let i = 0; i < 12; i++) {
    if (i >= yearChangeValueOfI) {
      currentYear = data?.startFromYear + 1;
    }
    xAxisValues.push({ label: getXaxisLabels(currentMonth, currentYear) });
    currentMonth = (currentMonth % 12) + 1;
  }

  return {
    costTrendData,
    xAxisValues,
  };
};

export const getSubtitleForSavingsTable = (
  drugCount: number,
  fills: number,
  planPaid: number,
  benchmarkCost: number,
  decimals: number = 2,
) => {
  return `This program sourced ${drugCount} ${
    drugCount > 1 ? 'drugs' : 'drug'
  }, filled ${fills} prescriptions, incurred a total plan paid of ${numberToCurrencyFormatter(
    planPaid,
    true,
    decimals,
  )}${
    benchmarkCost > 0
      ? `, and achieved savings of ${numberToCurrencyFormatter(
          benchmarkCost,
          true,
          decimals,
        )}.`
      : '.'
  } `;
};

const cellStyles = {
  fontSize: '14px',
  lineHeight: '23px',
  color: patientAccessTheme.palette.text.mediumemphasis,
  fontWeight: 400,
};

const savingsProgramColumns = (columnName: string): Columns[] => {
  return [
    {
      name: 'Drug Name',
      align: 'left',
      cellStyles: cellStyles,
      width: '200px',
      showEllipsis: true,
      ellipsisWidth: '200px',
    },
    {
      name: 'Therapeutic Class',
      align: 'left',
      cellStyles: cellStyles,
      width: '200px',
      showEllipsis: true,
      ellipsisWidth: '200px',
    },
    {
      name: 'No. of fills',
      align: 'center',
      cellStyles: cellStyles,
      width: '200px',
    },
    {
      name: 'Total Cost',
      align: 'right',
      cellStyles: cellStyles,
      width: '180px',
    },
    {
      name: `Benchmark Cost(${columnName})`,
      align: 'right',
      cellStyles: cellStyles,
      width: '310px',
    },
    {
      name: 'Plan Paid',
      align: 'right',
      cellStyles: cellStyles,
      width: '180px',
    },
    {
      name: 'Savings',
      align: 'center',
      cellStyles: cellStyles,
      width: '260px',
    },
  ];
};

export const getSavingsProgramColumns = (cost: number, columnName: string) => {
  if (cost > 0) {
    return savingsProgramColumns(columnName);
  } else {
    const newColumns = [...savingsProgramColumns('')];

    newColumns.splice(6, 1);
    newColumns.splice(4, 1);

    return newColumns;
  }
};

export const getTotalPages = (count: number) => {
  return Math.ceil(count / 10);
};

export const formatSavingsTableData = (data: any, cost: any) => {
  const tableData: any[] = [];
  data?.map((item: any) => {
    const tempData: any[] = [];
    tempData.push(item?.drugName);
    tempData.push(item?.atcLevel ? item?.atcLevel : '-');
    tempData.push(item?.claimsCount);
    tempData.push(numberToCurrencyFormatter(item?.totalCost, true, 2));
    if (cost > 0) {
      tempData.push(numberToCurrencyFormatter(item?.benchmark_cost, true, 2));
    }
    tempData.push(numberToCurrencyFormatter(item?.planPaid, true, 2));
    if (cost > 0) {
      tempData.push(
        <div
          style={{
            width: '100px',
            backgroundColor: patientAccessTheme.palette.AIQChips?.background2,
            border: '0.3px solid rgba(0, 130, 114, 0.5)',
            height: '25px',
            borderRadius: '4px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '5px 8px',
            margin: 'auto',
          }}
        >
          <Typography
            fontSize="12px"
            lineHeight="15px"
            fontType="medium"
            textColor={patientAccessTheme.palette.AIQChips?.textColor2}
          >
            {numberToCurrencyFormatter(item?.saving_cost, true, 2)}
          </Typography>
        </div>,
      );
    }

    tableData.push(tempData);
  });
  return tableData;
};
export const formatSavingsTable = (data: any) => {
  return data?.map((item: any, index: number) => {
    return {
      ...item,
      id: `table${index + 1}`,
      loading: false,
      pageNumber: 1,
    };
  });
};
